import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { RewardChip } from '@/features/guide-rewards/components/RewardChip';
import type { MerchStatus } from '@/features/guide-rewards/types';
import { toCsv } from '@/shared/utils/csv';
import { downloadCsvFile } from '@/shared/utils/downloadFile';
import { useRewardsToast } from '@/features/guide-rewards/components/RewardsToast';
import { adminGrowthApi, adminGrowthKeys, type AdminMerchRequest } from '../api/admin-growth.api';
import { FilterBar, GrowthTable, fmtDate, fmtDateTime, humanize, useGrowthMutation } from './GrowthShared';

/** Mirrors the server's ADMIN_TRANSITIONS so admins only see valid moves. */
const NEXT: Record<MerchStatus, MerchStatus[]> = {
  REQUESTED: ['APPROVED', 'REJECTED', 'OUT_OF_STOCK', 'CANCELLED'],
  OUT_OF_STOCK: ['APPROVED', 'REJECTED', 'CANCELLED'],
  APPROVED: ['PROCESSING', 'CANCELLED'],
  PROCESSING: ['SHIPPED', 'CANCELLED'],
  SHIPPED: ['DELIVERED'],
  DELIVERED: ['RECEIVED'],
  RECEIVED: [],
  REJECTED: [],
  CANCELLED: [],
};

const guideName = (r: AdminMerchRequest) =>
  [r.guideUserId?.firstName, r.guideUserId?.lastName].filter(Boolean).join(' ') || r.guideUserId?.email || 'Guide';

function InventoryDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data } = useQuery({ queryKey: adminGrowthKeys.inventory(), queryFn: adminGrowthApi.inventory, enabled: open });
  const [sizes, setSizes] = useState<{ size: string; stock: string }[]>([]);
  const [note, setNote] = useState('');
  const [active, setActive] = useState(true);

  useEffect(() => {
    if (!open) return;
    setSizes((data?.sizes ?? []).map((s) => ({ size: s.size, stock: String(s.stock) })));
    setNote(data?.sizeChartNote ?? '');
    setActive(data?.isActive ?? true);
  }, [open, data]);

  const save = useGrowthMutation(
    () =>
      adminGrowthApi.updateInventory({
        sizes: sizes.filter((s) => s.size.trim()).map((s) => ({ size: s.size.trim().toUpperCase(), stock: Number(s.stock || 0) })),
        sizeChartNote: note,
        isActive: active,
      }),
    'Inventory saved'
  );

  return (
    <Dialog open={open} onClose={save.isPending ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>T-shirt inventory</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
        <Typography variant="body2" color="text.secondary">Stock decreases when you approve a request and returns if an approved request is cancelled.</Typography>
        {sizes.map((row, i) => (
          <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 1.5, alignItems: 'center' }}>
            <TextField size="small" label="Size" value={row.size} onChange={(e) => setSizes((s) => s.map((r, j) => (j === i ? { ...r, size: e.target.value } : r)))} />
            <TextField size="small" type="number" label="In stock" value={row.stock} inputProps={{ min: 0 }} onChange={(e) => setSizes((s) => s.map((r, j) => (j === i ? { ...r, stock: e.target.value } : r)))} />
            <IconButton aria-label={`Remove size ${row.size}`} onClick={() => setSizes((s) => s.filter((_, j) => j !== i))}><DeleteOutlineIcon /></IconButton>
          </Box>
        ))}
        <Button startIcon={<AddIcon />} onClick={() => setSizes((s) => [...s, { size: '', stock: '0' }])} sx={{ justifySelf: 'start' }}>Add size</Button>
        <TextField label="Size chart (from the manufacturer)" value={note} onChange={(e) => setNote(e.target.value)} multiline minRows={3} helperText="Shown to guides when they pick a size. Leave blank until you have the real chart." />
        <FormControlLabel control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />} label="Accept new T-shirt requests" />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={save.isPending}>Cancel</Button>
        <Button variant="contained" disabled={save.isPending || sizes.length === 0} onClick={() => save.mutate(undefined, { onSuccess: onClose })}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

function TransitionDialog({ request, onClose }: { request: AdminMerchRequest | null; onClose: () => void }) {
  const [status, setStatus] = useState<MerchStatus | ''>('');
  const [note, setNote] = useState('');
  const [courier, setCourier] = useState('');
  const [trackingId, setTrackingId] = useState('');
  useEffect(() => {
    setStatus('');
    setNote('');
    setCourier(request?.dispatch?.courier ?? '');
    setTrackingId(request?.dispatch?.trackingId ?? '');
  }, [request]);

  const move = useGrowthMutation(
    () => adminGrowthApi.transitionMerch(request!._id, { status: status as MerchStatus, note: note || undefined, courier: courier || undefined, trackingId: trackingId || undefined }),
    'Request updated'
  );
  const options = request ? NEXT[request.status] : [];

  return (
    <Dialog open={Boolean(request)} onClose={move.isPending ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>T-shirt request</DialogTitle>
      {request && (
        <DialogContent sx={{ display: 'grid', gap: 2 }}>
          <Box>
            <Typography fontWeight={700}>{guideName(request)} · size {request.size}</Typography>
            <Typography variant="body2" color="text.secondary">
              {request.address.line1}{request.address.line2 ? `, ${request.address.line2}` : ''}, {request.address.city}, {request.address.state} {request.address.pincode} · {request.contactPhone}
            </Typography>
            {request.instructions && <Typography variant="body2" sx={{ mt: 0.5 }}>Note: {request.instructions}</Typography>}
          </Box>
          <Box>
            {request.statusHistory.map((h, i) => (
              <Typography key={i} variant="caption" display="block" color="text.secondary">
                {humanize(h.status)} · {fmtDateTime(h.at)}{h.note ? ` — ${h.note}` : ''}
              </Typography>
            ))}
          </Box>
          {options.length === 0 ? (
            <Alert severity="info">This request is {humanize(request.status).toLowerCase()} — no further steps.</Alert>
          ) : (
            <>
              <TextField select label="Move to" value={status} onChange={(e) => setStatus(e.target.value as MerchStatus)}>
                {options.map((s) => <MenuItem key={s} value={s}>{humanize(s)}</MenuItem>)}
              </TextField>
              {status === 'SHIPPED' && (
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>
                  <TextField label="Courier" value={courier} onChange={(e) => setCourier(e.target.value)} />
                  <TextField label="Tracking ID" value={trackingId} onChange={(e) => setTrackingId(e.target.value)} />
                </Box>
              )}
              <TextField label={status === 'REJECTED' ? 'Reason (shown to the guide)' : 'Note (optional)'} value={note} onChange={(e) => setNote(e.target.value)} multiline minRows={2} />
            </>
          )}
        </DialogContent>
      )}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={move.isPending}>Close</Button>
        {options.length > 0 && (
          <Button variant="contained" disabled={!status || move.isPending || (status === 'REJECTED' && note.trim().length < 3)} onClick={() => move.mutate(undefined, { onSuccess: onClose })}>
            Update
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export function GrowthMerchPanel() {
  const notify = useRewardsToast();
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);
  const [status, setStatus] = useState('');
  const [size, setSize] = useState('');
  const [selected, setSelected] = useState<AdminMerchRequest | null>(null);
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const inventory = useQuery({ queryKey: adminGrowthKeys.inventory(), queryFn: adminGrowthApi.inventory });

  const query = { page: page + 1, limit, status: status || undefined, size: size || undefined };
  const { data, isLoading, isError } = useQuery({
    queryKey: adminGrowthKeys.merch(query),
    queryFn: () => adminGrowthApi.merchRequests(query),
    placeholderData: keepPreviousData,
  });

  const exportCsv = async () => {
    setExporting(true);
    try {
      const rows = await adminGrowthApi.merchExport();
      const csv = toCsv([
        ['Requested', 'Guide', 'Email', 'Phone', 'Size', 'Status', 'Address', 'City', 'State', 'PIN', 'Instructions', 'Courier', 'Tracking ID'],
        ...rows.map((r) => [
          fmtDate(r.createdAt), guideName(r), r.guideUserId?.email ?? '', r.contactPhone, r.size, r.status,
          [r.address.line1, r.address.line2].filter(Boolean).join(', '), r.address.city, r.address.state, r.address.pincode,
          r.instructions ?? '', r.dispatch?.courier ?? '', r.dispatch?.trackingId ?? '',
        ]),
      ]);
      downloadCsvFile(csv, `trplin-tshirt-requests-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch {
      notify('Export failed. Please try again.', 'error');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        {(inventory.data?.sizes ?? []).map((s) => (
          <RewardChip key={s.size} label={`${s.size}: ${s.stock}`} tone={s.stock > 0 ? 'neutral' : 'warning'} />
        ))}
        {inventory.data && !inventory.data.isActive && <RewardChip label="Requests closed" tone="error" />}
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<Inventory2OutlinedIcon />} onClick={() => setInventoryOpen(true)}>Manage inventory</Button>
        <Button startIcon={<DownloadIcon />} onClick={exportCsv} disabled={exporting}>{exporting ? 'Exporting…' : 'Export CSV'}</Button>
      </Box>
      <FilterBar>
        <TextField size="small" select label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
          <MenuItem value="">All</MenuItem>
          {Object.keys(NEXT).map((s) => <MenuItem key={s} value={s}>{humanize(s)}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Size" value={size} onChange={(e) => { setSize(e.target.value); setPage(0); }}>
          <MenuItem value="">All</MenuItem>
          {(inventory.data?.sizes ?? []).map((s) => <MenuItem key={s.size} value={s.size}>{s.size}</MenuItem>)}
        </TextField>
      </FilterBar>
      <GrowthTable
        loading={isLoading}
        error={isError}
        rows={data?.items}
        rowKey={(r) => r._id}
        onRowClick={setSelected}
        emptyText="No T-shirt requests match these filters."
        page={page}
        limit={limit}
        total={data?.total}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(0); }}
        columns={[
          { key: 'guide', label: 'Guide', render: (r) => <><Typography fontWeight={600} fontSize="0.875rem">{guideName(r)}</Typography><Typography variant="caption" color="text.secondary">{r.contactPhone}</Typography></> },
          { key: 'size', label: 'Size', render: (r) => <b>{r.size}</b> },
          { key: 'city', label: 'Ship to', render: (r) => `${r.address.city}, ${r.address.state}` },
          { key: 'status', label: 'Status', render: (r) => <RewardChip label={humanize(r.status)} status={r.status} /> },
          { key: 'tracking', label: 'Tracking', render: (r) => r.dispatch?.trackingId ?? '—' },
          { key: 'created', label: 'Requested', render: (r) => fmtDate(r.createdAt) },
        ]}
      />
      <TransitionDialog request={selected} onClose={() => setSelected(null)} />
      <InventoryDialog open={inventoryOpen} onClose={() => setInventoryOpen(false)} />
    </>
  );
}
